import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Debug logging for auth service
console.log('🔐 Auth service API_BASE_URL:', API_BASE_URL);

const authApi = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

console.log('🔐 Auth service axios instance baseURL:', authApi.defaults.baseURL);

// Add token to requests if available
authApi.interceptors.request.use((config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle token expiration
authApi.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('access_token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

const authService = {
    // Check if root setup is required
    checkSetup: async () => {
        const response = await authApi.get('/auth/check-setup');
        return response.data;
    },

    // Setup root user
    setupRoot: async (setupData) => {
        const response = await authApi.post('/auth/setup-root', setupData);
        const { access_token, user } = response.data;
        
        // Store token and user info
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('user', JSON.stringify(user));
        
        return response.data;
    },

    // Login
    login: async (credentials) => {
        const formData = new FormData();
        formData.append('username', credentials.username);
        formData.append('password', credentials.password);
        
        const response = await authApi.post('/auth/login', formData, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        });
        
        const { access_token, user } = response.data;
        
        // Store token and user info
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('user', JSON.stringify(user));
        
        return response.data;
    },

    // Logout
    logout: () => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
    },

    // Get current user info
    getCurrentUser: async () => {
        const response = await authApi.get('/auth/me');
        return response.data;
    },

    // Get stored user from localStorage
    getStoredUser: () => {
        const user = localStorage.getItem('user');
        return user ? JSON.parse(user) : null;
    },

    // Check if user is authenticated
    isAuthenticated: () => {
        return !!localStorage.getItem('access_token');
    },

    // Check if user is admin
    isAdmin: () => {
        const user = authService.getStoredUser();
        return user && user.role === 'admin';
    },

    // Annotators for the inter-annotator-agreement study are confined to the
    // annotation page. They must not reach the dashboard or the ground-truth
    // pages: both publish the existing verdicts and the precision figures,
    // which would tell an annotator the expected answer distribution before
    // they start and invalidate the agreement measurement.
    isAnnotator: () => {
        const user = authService.getStoredUser();
        return !!user && user.role === 'annotator';
    },

    // Separate from annotator: a classifier judges finding provenance, never
    // control mappings, and must not see the running split -- watching one
    // verdict pull ahead would anchor the judgements still to be made.
    isClassifier: () => {
        const user = authService.getStoredUser();
        return !!user && user.role === 'classifier';
    },

    // User management (admin only)
    createUser: async (userData) => {
        const response = await authApi.post('/auth/users', userData);
        return response.data;
    },

    listUsers: async () => {
        const response = await authApi.get('/auth/users');
        return response.data;
    },

    updateUser: async (userId, userData) => {
        const response = await authApi.put(`/auth/users/${userId}`, userData);
        return response.data;
    },

    deleteUser: async (userId) => {
        const response = await authApi.delete(`/auth/users/${userId}`);
        return response.data;
    },

    // Validation endpoints
    validatePassword: async (password) => {
        const response = await authApi.get(`/auth/validate-password?password=${encodeURIComponent(password)}`);
        return response.data;
    },

    validateUsername: async (username) => {
        const response = await authApi.get(`/auth/validate-username?username=${encodeURIComponent(username)}`);
        return response.data;
    },
};

export default authService; 